import { skipToken } from '@reduxjs/toolkit/query'
import { useCallback, useMemo } from 'react'
import { routes } from '@/routes'
import type { ManageReservationCancelResult } from '@sevenrooms/domain'
import { useNavigation } from '@sevenrooms/navigation'
import { HTMLContent } from '@sevenrooms/ui-kit/core'
import type { ModalHeaderProps } from '@sevenrooms/ui-kit/layout'
import { useManageReservationRoute } from '../../../hooks'
import { useCancelReservationMutation, useGetManageReservationDetailsQuery, useModals } from '../../../store'
import { BaseCancelModal } from '../BaseCancelModal'
import { CancellationFeeModal } from '../CancellationFeeModal'
import { RefundModal } from '../RefundModal'
import { UnableCancelModal } from '../UnableCancelModal'

interface CancelSurfaceProps {
  closeHref: NonNullable<ModalHeaderProps['closeHref']>
}

export function CancelModal({ closeHref }: CancelSurfaceProps) {
  const { showErrorModal } = useModals()
  const { closeSurface, push } = useNavigation()
  const { lang, token, venueKey } = useManageReservationRoute()

  const { data, isLoading } = useGetManageReservationDetailsQuery(token ? { lang, token } : skipToken)
  const [cancelReservation, { isLoading: isCanceling }] = useCancelReservationMutation()
  const { policies, actualFlags, cancelDetails, refundDetails } = data ?? {}
  const cancellationPolicy = useMemo(
    () => (policies?.cancellationPolicy ? <HTMLContent content={policies?.cancellationPolicy} allowedTags={false} /> : null),
    [policies?.cancellationPolicy]
  )

  const onCancelClick = useCallback(async () => {
    try {
      const result: ManageReservationCancelResult = await cancelReservation({ token: token as string, lang }).unwrap()
      if (result.actualFlags.hasPassed) {
        closeSurface(closeHref)
        push(routes.reservations.manage.passed, {
          params: { venueKey },
        })
      } else if (result.isCanceled) {
        closeSurface(closeHref)
        push(routes.reservations.manage.canceled, {
          params: { venueKey },
        })
      }
    } catch {
      showErrorModal()
    }
  }, [cancelReservation, token, lang, closeSurface, closeHref, push, venueKey, showErrorModal])

  if (isLoading) {
    return null
  }

  if (!actualFlags?.isCancelable) {
    return <UnableCancelModal closeHref={closeHref} />
  }

  if (cancelDetails) {
    return (
      <CancellationFeeModal
        amount={cancelDetails.cancellationChargeAmountDecimal}
        cardBrand={cancelDetails.cancellationCardInfo.brand}
        cardLast4={cancelDetails.cancellationCardInfo.last4}
        cancellationPolicy={cancellationPolicy}
        onCancelClick={onCancelClick}
        isCancelDisabled={isCanceling}
        closeHref={closeHref}
      />
    )
  }

  if (refundDetails) {
    return (
      <RefundModal
        charges={refundDetails.refundableCharges}
        cancellationPolicy={cancellationPolicy}
        isCancelDisabled={isCanceling}
        onCancelClick={onCancelClick}
        closeHref={closeHref}
      />
    )
  }

  return (
    <BaseCancelModal
      cancellationPolicy={cancellationPolicy}
      closeHref={closeHref}
      isCancelDisabled={isCanceling}
      onCancelClick={onCancelClick}
    />
  )
}
